export type InitialSceneState = 'ready' | 'cancelled' | 'timed-out';

export class InitialSceneTimeout extends Error {
  public constructor() {
    super('The authoritative train scene did not arrive during preparation.');
    this.name = 'InitialSceneTimeout';
  }
}

/** Per-Game latch, never a cached snapshot from another lobby. Shader loading
 * must include the first owned authoritative scene, not just the empty train.
 * Waiting has a bounded lifetime and cannot survive disposal. */
export class InitialSceneReadiness {
  private ready: boolean;
  private readonly waiters = new Set<(result: InitialSceneState) => void>();

  public constructor(ready = false) { this.ready = ready; }

  public get isReady(): boolean { return this.ready; }

  public markReady(): boolean {
    if (this.ready) return false;
    this.ready = true;
    this.waiters.forEach(finish => finish('ready'));
    return true;
  }

  public wait(signal: AbortSignal): Promise<InitialSceneState> {
    if (signal.aborted) return Promise.resolve('cancelled');
    if (this.ready) return Promise.resolve('ready');
    return new Promise(resolve => {
      const finish = (result: InitialSceneState): void => {
        clearTimeout(timer);
        signal.removeEventListener('abort', cancel);
        this.waiters.delete(finish);
        resolve(result);
      };
      const cancel = (): void => finish('cancelled');
      const timer = setTimeout(() => finish('timed-out'), 10_000);
      this.waiters.add(finish);
      signal.addEventListener('abort', cancel, { once: true });
    });
  }
}
